import React, { useState } from 'react';
import { 
  FileSpreadsheet, Calendar, User, Building, Plus, Save, 
  Printer, X, Landmark, CreditCard, Trash2, CheckCircle, 
  Layers, ChevronDown, RefreshCw, FileText
} from 'lucide-react';

export default function RSRV() {
  // Transaction meta states
  const [receiptType, setReceiptType] = useState('EJARI'); // CASH, DEPOSIT, EJARI, COMMISSION
  const [chequeFirst, setChequeFirst] = useState(false);
  
  // Ledger Line Item Rows
  const [lineItems, setLineItems] = useState([
    { id: 1, slno: '1', account: '40020001', name: 'Al Manarah - Rent Income', desc: 'Q3 Advance Rental', type: 'CR', amount: '56300.00', chqNo: 'CHQ-9921', chqDate: '2026-07-02', bank: 'Emirates NBD' }
  ]);

  // Master total counters
  const totalDebit = lineItems.reduce((acc, curr) => acc + (curr.type === 'DR' ? parseFloat(curr.amount || 0) : 0), 0).toFixed(2);
  const totalCredit = lineItems.reduce((acc, curr) => acc + (curr.type === 'CR' ? parseFloat(curr.amount || 0) : 0), 0).toFixed(2);

  // Quick Action: Add new item line
  const addNewLine = () => {
    setLineItems([...lineItems, {
      id: Date.now(),
      slno: (lineItems.length + 1).toString(),
      account: '',
      name: '',
      desc: '',
      type: 'CR',
      amount: '0.00',
      chqNo: '',
      chqDate: '',
      bank: ''
    }]);
  };

  const removeLine = (id) => {
    if(lineItems.length === 1) return;
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans antialiased flex flex-col">
      
      {/* Dynamic Command Top Ribbon Header */}
      <header className="bg-slate-900 text-white h-16 min-h-16 flex items-center justify-between px-6 border-b border-slate-800 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-600 p-2 rounded-lg text-white">
            <FileSpreadsheet size={18} />
          </div>
          <div>
            <h1 className="text-sm font-bold tracking-tight uppercase">Receipt Voucher Workspace</h1>
            <p className="text-[11px] text-slate-400">R&K Accounts Ledger Management</p>
          </div>
        </div>

        {/* Payment Channel Toggle Badges */}
        <div className="flex items-center gap-2 bg-slate-800 p-1 rounded-xl border border-slate-700">
          {['CASH', 'DEPOSIT', 'EJARI', 'COMMISSION'].map((type) => (
            <button
              key={type}
              onClick={() => setReceiptType(type)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold tracking-wider transition-all ${
                receiptType === type 
                  ? 'bg-indigo-600 text-white shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        {/* Side Modifiers */}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-300 select-none">
            <input 
              type="checkbox" 
              checked={chequeFirst} 
              onChange={(e) => setChequeFirst(e.target.checked)} 
              className="rounded bg-slate-800 border-slate-700 text-indigo-600 focus:ring-0 w-4 h-4"
            />
            Cheque First Allocation
          </label>
          <button className="p-1.5 text-slate-400 hover:text-white rounded bg-slate-800 border border-slate-700 transition-colors">
            <RefreshCw size={14} />
          </button>
        </div>
      </header>

      {/* Primary Dashboard Panel Core */}
      <main className="flex-1 p-6 space-y-6 max-w-[1700px] w-full mx-auto overflow-y-auto">
        
        {/* ROW 1: Voucher Metadata Registry Grid */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">RV Voucher No</label>
            <div className="relative">
              <input type="text" defaultValue="1025" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Voucher Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 text-slate-400" size={15} />
              <input type="date" defaultValue="2026-07-02" className="w-full pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Associated Tenant Account</label>
            <div className="relative">
              <User className="absolute left-3 top-2.5 text-slate-400" size={15} />
              <input type="text" defaultValue="12051753 — AKHIL RAJAN" className="w-full pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Target Clearing Bank</label>
            <div className="relative">
              <Landmark className="absolute left-3 top-2.5 text-slate-400" size={15} />
              <select className="w-full pl-9 pr-3 py-1.5 border border-slate-200 bg-white rounded-lg text-sm appearance-none focus:ring-2 focus:ring-indigo-500">
                <option>Emirates NBD Central</option>
                <option>Abu Dhabi Commercial Bank</option>
                <option>Mashreq Corporate</option>
              </select>
              <ChevronDown className="absolute right-3 top-3 text-slate-400 pointer-events-none" size={12} />
            </div>
          </div>
        </div>

        {/* ROW 2: Interactive Double-Entry Transaction Ledger Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col">
          <div className="px-5 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">Transactional Allocation Breakdowns</h2>
            <button 
              onClick={addNewLine}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Plus size={14} /> Append Ledger Row
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1200px]">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="py-3 px-4 w-12 text-center">SL</th>
                  <th className="py-3 px-4 w-36">GL Account</th>
                  <th className="py-3 px-4 w-52">Account Name Reference</th>
                  <th className="py-3 px-4">Statement Description</th>
                  <th className="py-3 px-4 w-24">Type</th>
                  <th className="py-3 px-4 w-36">Value Amount</th>
                  <th className="py-3 px-4 w-32">Cheque No</th>
                  <th className="py-3 px-4 w-36">Cheque Date</th>
                  <th className="py-3 px-4 w-44">Issuing Bank</th>
                  <th className="py-3 px-4 w-12 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {lineItems.map((item, index) => (
                  <tr key={item.id} className="hover:bg-slate-50/40 transition-colors group">
                    <td className="py-3 px-4 font-mono text-center text-slate-400">{index + 1}</td>
                    <td className="py-2 px-2">
                      <input type="text" defaultValue={item.account} className="w-full px-2 py-1.5 border border-slate-200 rounded font-mono font-medium text-slate-700 focus:ring-1 focus:ring-indigo-500" placeholder="e.g. 4002" />
                    </td>
                    <td className="py-2 px-2">
                      <input type="text" defaultValue={item.name} className="w-full px-2 py-1.5 border border-slate-200 rounded font-medium text-slate-800 focus:ring-1 focus:ring-indigo-500" placeholder="Acct Description" />
                    </td>
                    <td className="py-2 px-2">
                      <input type="text" defaultValue={item.desc} className="w-full px-2 py-1.5 border border-slate-200 rounded focus:ring-1 focus:ring-indigo-500" placeholder="Allocation comments..." />
                    </td>
                    <td className="py-2 px-2">
                      <select defaultValue={item.type} className="w-full px-2 py-1.5 border border-slate-200 rounded bg-white font-bold text-slate-700 focus:ring-1 focus:ring-indigo-500">
                        <option value="CR">CR (Credit)</option>
                        <option value="DR">DR (Debit)</option>
                      </select>
                    </td>
                    <td className="py-2 px-2">
                      <div className="relative">
                        <input type="text" defaultValue={item.amount} className="w-full pl-2 pr-7 py-1.5 border border-slate-200 rounded text-right font-mono font-bold text-slate-800 focus:ring-1 focus:ring-indigo-500" />
                        <span className="absolute right-2 top-2 text-[9px] text-slate-400 font-bold">AED</span>
                      </div>
                    </td>
                    <td className="py-2 px-2">
                      <input type="text" defaultValue={item.chqNo} className="w-full px-2 py-1.5 border border-slate-200 rounded font-mono text-slate-600 focus:ring-1 focus:ring-indigo-500" placeholder="—" />
                    </td>
                    <td className="py-2 px-2">
                      <input type="date" defaultValue={item.chqDate} className="w-full px-2 py-1 border border-slate-200 rounded font-mono text-slate-600 focus:ring-1 focus:ring-indigo-500" />
                    </td>
                    <td className="py-2 px-2">
                      <input type="text" defaultValue={item.bank} className="w-full px-2 py-1.5 border border-slate-200 rounded text-slate-600 focus:ring-1 focus:ring-indigo-500" placeholder="Bank Title" />
                    </td>
                    <td className="py-2 px-2 text-center">
                      <button 
                        onClick={() => removeLine(item.id)}
                        disabled={lineItems.length === 1}
                        className="p-1 text-slate-400 hover:text-rose-600 disabled:opacity-30 disabled:hover:text-slate-400 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Ledger Control Aggregations & Summary Fields */}
          <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">PDC Asset A/c:</span>
                <span className="font-mono bg-white border border-slate-200 rounded px-2 py-1 font-bold text-slate-700">11030001</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">PDC Provision A/c:</span>
                <span className="font-mono bg-white border border-slate-200 rounded px-2 py-1 font-bold text-slate-700">31030001</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-right shadow-3xs">
                <span className="block text-[9px] font-bold text-emerald-600 uppercase tracking-wider">Total Debit</span>
                <span className="font-mono font-extrabold text-sm text-slate-800">{totalDebit} AED</span>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-right shadow-3xs">
                <span className="block text-[9px] font-bold text-indigo-600 uppercase tracking-wider">Total Credit</span>
                <span className="font-mono font-extrabold text-sm text-slate-800">{totalCredit} AED</span>
              </div>
            </div>
          </div>
        </div>

        {/* ROW 3: Historical Audit Log / Lower Journal List */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <FileText size={14} className="text-slate-400" /> Recent Batch Receipt Log
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50/30 border-b border-slate-200 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="py-2.5 px-5">RV No</th>
                  <th className="py-2.5 px-5">Clearing Date</th>
                  <th className="py-2.5 px-5">Customer Entity / Tenant</th>
                  <th className="py-2.5 px-5">Internal Remarks</th>
                  <th className="py-2.5 px-5 text-right">Settled Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                <tr className="bg-slate-50/40 hover:bg-slate-50 transition-colors">
                  <td className="py-3 px-5 font-mono text-indigo-600 font-bold">1024</td>
                  <td className="py-3 px-5 text-slate-500 font-mono">02-Jul-2026</td>
                  <td className="py-3 px-5 text-slate-900">AKHIL RAJAN</td>
                  <td className="py-3 px-5 text-slate-500">TC NoJULY02 pai • JULY02 Allocation</td>
                  <td className="py-3 px-5 text-right font-mono font-bold text-slate-800">56300.00 AED</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </main>

      {/* Persistent Global Action Command Bar */}
      <footer className="bg-white border-t border-slate-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-3 shadow-md sticky bottom-0">
        <button className="text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors py-2 px-2">
          Discard Draft
        </button>

        <div className="w-full sm:w-auto flex items-center gap-2">
          <button className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-bold transition-all shadow-3xs">
            <Printer size={14} /> Print Receipt
          </button>
          <button className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm">
            <Save size={14} /> Commit & Post Ledger
          </button>
        </div>
      </footer>

    </div>
  );
}